import { createContext, ReactNode, useEffect, useRef, useState } from "react";
import { boundingBox, excludeIds, fetchAvailableLayers } from "../utils/dashboardUtils";
import { BoundingBox, LayerData } from "../services/types";
import { useOrgContext } from "./OrgIdContext";
import { layersEnv } from "../utils/environment";

// Define the shape of your context data
interface LayersContextType {
    layers: LayerData[];
    loading: boolean;
    fetchLayersData: (orgId: number, locationId: string, boundingBox: BoundingBox | null, excludeIds: string[] | null) => void;
    refreshLayers: () => void,
}

// Create the context with default values
const LayersContext = createContext<LayersContextType>({
    layers: [],
    loading: true,
    fetchLayersData: () => {},
    refreshLayers: () => {},
});

interface LayersProviderProps {
    children: ReactNode;
}

// Define the provider for the context
export const LayersProvider = ({ children }: LayersProviderProps) => {
    const { orgId, locationId } = useOrgContext();
    const [layers, setLayers] = useState<LayerData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [lastFetchParams, setLastFetchParams] = useState<{
        orgId: number;
        locationId: string;
        boundingBox: BoundingBox | null;
        excludeIds: string[] | null;
    } | null>(null);

    // Use ref to prevent triggering re-renders
    const availableLayersRef = useRef<string | null>(null);

    // Fetch availableLayers when locationId changes
    useEffect(() => {
        const fetchLayersMeta = async () => {
            if (locationId) {
                const result = await fetchAvailableLayers(locationId);
                availableLayersRef.current = result;
                console.log('Fetched availableLayers:', result);
            }
        };
        fetchLayersMeta();
    }, [locationId]);

    const fetchLayersData = async (orgId: number, locationId: string, boundingBox: BoundingBox | null, excludeIds: string[] | null) => {
        if (!orgId) {
            setLoading(false);
            console.log('No orgId provided to fetch layers');
            return;
        }

        console.log('org id and location id in layers context:', orgId, locationId);
        setLastFetchParams({ orgId, locationId, boundingBox, excludeIds });

        setLoading(true);
        try {
            const availableLayers = availableLayersRef.current;

            if (!availableLayers) {
                // If "none", set layers to an empty array and exit
                setLayers([]);
                setLoading(false);
                return;
            }

            // Fetch layers
            const layersResponse = await fetch(
                `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayersbysearch?orgId=${orgId}&environment=${layersEnv}&externalId=${availableLayers}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!layersResponse.ok) {
                throw new Error('Failed to fetch layers');
            }

            const layersResult = await layersResponse.json();
            console.log('layers result:', layersResult);

            let fetchedLayers: LayerData[] = (layersResult.data || []).sort((a: LayerData, b: LayerData) => {
                const aLayerIdExt = a.layer_id_ext || '';
                const bLayerIdExt = b.layer_id_ext || '';

                return aLayerIdExt.localeCompare(bLayerIdExt);
            });

            if (availableLayers && !availableLayers.includes('*')) {
                // Split the string into an array of strings
                const allowedIds = availableLayers.split(',').map(layer => layer.trim());

                fetchedLayers = fetchedLayers.filter(layer =>
                    allowedIds.includes(layer.layer_id_ext)
                );
            }

            // Update state with filtered layers (without items initially)
            setLayers(fetchedLayers);

            if (fetchedLayers.length > 0) {
                // Construct boundingBox query parameters
                const boundingBoxParams = boundingBox 
                ? `&boundingBox=${encodeURIComponent(JSON.stringify(boundingBox))}` 
                : "";

                const excludeIdsParam = excludeIds ? `&excludeIds=${excludeIds.join(",")}` : "";

                // Fetch items for all layers concurrently
                const itemsPromises = fetchedLayers.map(layer => 
                    fetch(
                        `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getbasiclayeritems?layerId=${layer.layer_id}&environment=${layersEnv}${boundingBoxParams}${excludeIdsParam}`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    )
                    .then(response => response.ok ? response.json() : Promise.reject('Failed to fetch layer items'))
                    .catch(error => {
                        console.error(`Error fetching items for layer ${layer.layer_id}:`, error);
                        return [{ items: [], summary: [] }]; // Ensure failure doesn't break entire function
                    })
                );

                // Wait for all layer fetches to complete
                const itemsResults = await Promise.all(itemsPromises);

                // Update each layer with its corresponding items
                const layersWithItems = fetchedLayers.map((layer, index) => ({
                    ...layer,
                    items: itemsResults[index].items,
                    summary: itemsResults[index].summary,
                }));

                setLayers(layersWithItems);
            }
        } catch (error: any) {
            console.log('Error fetching layers or items:', error.message);
            setLayers([]);
        } finally {
            setLoading(false);
        }
    };

    const refreshLayers = () => {
        if (lastFetchParams) {
            const { orgId, locationId, boundingBox, excludeIds } = lastFetchParams;
            fetchLayersData(orgId, locationId, boundingBox, excludeIds);
        } else {
            console.warn("No previous fetch params found — skipping refresh");
        }
    };

    useEffect(() => {
        if (orgId && locationId) {
            fetchLayersData(orgId, locationId, boundingBox, excludeIds);
        }
    }, [orgId, locationId, boundingBox, excludeIds]);

    return (
        <LayersContext.Provider value={{ layers, loading, fetchLayersData, refreshLayers }}>
            {children}
        </LayersContext.Provider>
    );
};

// Export the hook to use the context
export default LayersContext;